"use client";

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import * as React from "react";
import { useEffect, useId, useState } from "react";

import { useScopeClass } from "@/components/ui/scope";
import { cn } from "@/lib/utils";

interface FloatingSelectItem {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

interface FloatingSelectProps {
  /** Label rendered inside the field (Reddit-style floating). When a value is selected
   *  or the popup is open, the label animates up to the top-left and shrinks. Omit for
   *  an unlabeled trigger (e.g., inline filter). */
  label?: string;
  /** Destructive ring + message under the field. Sets aria-invalid on the trigger. */
  error?: string;
  /** Hint text rendered under the field. Suppressed if `error` is also set. */
  helperText?: string;
  /** Icon rendered absolutely on the left inside the trigger. Shifts content right. */
  leadingIcon?: React.ReactNode;
  /** Placeholder shown when no value is selected. Suppressed when `label` is set —
   *  the floating label IS the placeholder until the user picks something. */
  placeholder?: string;
  /** Items to render in the popup. Labels can be plain strings or rich React nodes. */
  items: ReadonlyArray<FloatingSelectItem>;
  /** Controlled selected value. */
  value?: string | null;
  /** Uncontrolled initial value. */
  defaultValue?: string | null;
  /** Fired when the user picks an item. */
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  /**
   * "outlined" (default) — full box ring, matching the standard form field style.
   * "standard" — no box border; a 2px green underline appears at the bottom only
   *   when the popup is open (mirrors the old MUI filled+no-border sort trigger).
   */
  variant?: "outlined" | "standard";
  "aria-describedby"?: string;
  "aria-label"?: string;
}

function FloatingSelect({
  id,
  label,
  error,
  helperText,
  leadingIcon,
  placeholder,
  items,
  value,
  defaultValue,
  onValueChange,
  disabled,
  required,
  name,
  className,
  variant = "outlined",
  "aria-describedby": userDescribedBy,
  "aria-label": ariaLabel,
}: FloatingSelectProps) {
  const reactId = useId();
  const triggerId = id ?? reactId;
  const helperId = `${triggerId}-helper`;
  const errorId = `${triggerId}-error`;
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string | null>(value ?? defaultValue ?? null);

  useEffect(() => {
    if (value !== undefined) setInternalValue(value);
  }, [value]);

  const hasLabel = !!label;
  const hasValue = internalValue !== null && internalValue !== "";
  const isFloating = open || hasValue;
  const invalid = !!error;
  const isAdmin = useScopeClass() === "admin-scope";
  const internalDescribedBy = error ? errorId : helperText ? helperId : undefined;
  const describedBy = [internalDescribedBy, userDescribedBy].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex flex-col gap-1 text-left">
      <SelectPrimitive.Root
        items={items}
        {...(value !== undefined ? { value } : { defaultValue })}
        onValueChange={(v: string | null) => {
          setInternalValue(v);
          if (v !== null) onValueChange?.(v);
        }}
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
        required={required}
        name={name}
      >
        <SelectPrimitive.Trigger
          id={triggerId}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          data-slot="floating-select"
          className={cn(
            "relative flex w-full items-center text-left text-foreground transition-shadow outline-none",
            "data-placeholder:text-muted-foreground",
            disabled && "cursor-not-allowed opacity-60",
            hasLabel
              ? isAdmin
                ? "h-14 pt-[23px] pb-[9px]"
                : "h-[58px] pt-[23px] pb-[9px]"
              : "h-11",
            leadingIcon ? "pl-9 pr-9" : "pl-3 pr-9",
            variant === "outlined" &&
              (isAdmin
                ? // Admin keeps the original rounded-2xl inset ring.
                  [
                    "rounded-2xl bg-transparent ring-1 ring-inset ring-input",
                    "focus-visible:ring-2 focus-visible:ring-ring",
                    invalid &&
                      "ring-destructive focus-visible:ring-destructive data-[popup-open]:ring-destructive",
                  ]
                : // Public site: MUI filled input — 4px radius, 1px border, white fill, green focus.
                  [
                    "rounded border border-input bg-white transition-colors focus-within:border-ring",
                    "data-[popup-open]:border-ring",
                    invalid &&
                      "border-destructive focus-within:border-destructive data-[popup-open]:border-destructive",
                  ]),
            variant === "standard" && [
              "rounded-none border-b-2 border-transparent",
              "data-[popup-open]:border-primary",
              invalid && "border-destructive",
            ],
            className,
          )}
        >
          {leadingIcon && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {leadingIcon}
            </span>
          )}
          <SelectPrimitive.Value
            data-slot="floating-select-value"
            placeholder={!hasLabel && placeholder ? placeholder : undefined}
            className="block flex-1 truncate text-base"
          />
          {hasLabel && (
            <span
              className={cn(
                "pointer-events-none absolute origin-left text-muted-foreground transition-all duration-150 ease-out",
                leadingIcon ? "left-9" : "left-3",
                // Floating size matches the old MUI shrunk label (0.9rem × 0.75 ≈ 10.8px).
                isFloating ? "top-[9px] text-[10.8px]" : "top-1/2 -translate-y-1/2 text-base",
                invalid && "text-destructive",
              )}
            >
              {label}
              {/* Matches FloatingInput — legacy's MUI fields appended the asterisk
                  whenever `required` was set. aria-hidden: the control's own
                  `required` (passed through above) is what gets announced. */}
              {required && <span aria-hidden> *</span>}
            </span>
          )}
          <SelectPrimitive.Icon
            render={
              <ChevronDownIcon
                aria-hidden
                className={cn(
                  "pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground",
                  variant === "standard" && "transition-transform [[data-popup-open]_&]:rotate-180",
                )}
              />
            }
          />
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Positioner
            sideOffset={6}
            // Plain popover placement. We tried MUI-style selected-item alignment
            // (alignItemWithTrigger) for the public site, but Base UI falls back to a popover
            // near the viewport edge anyway (where our selects sit) and *clips* short menus
            // that MUI would shift to fit — so popover placement diffs closer to old overall.
            alignItemWithTrigger={false}
            className="isolate z-50"
          >
            <SelectPrimitive.Popup
              data-slot="floating-select-content"
              className={cn(
                "min-w-(--anchor-width) origin-(--transform-origin) bg-popover text-popover-foreground",
                // Fade + zoom on open/close (matches the native AnchoredMenu's
                // fade+scale and the old MUI Select Grow transition). Applies to every
                // variant so the public-site sort/date selects animate, not just admin.
                "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
                "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
                "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
                // Admin caps the menu at ~9–10 rows then scrolls (plain popover). The public
                // site uses Base UI's alignItemWithTrigger, which manages its own height/scroll
                // via --available-height so the selected item can sit over the trigger (MUI parity).
                isAdmin
                  ? "max-h-[min(var(--available-height),354px)] overflow-x-hidden overflow-y-auto"
                  : "max-h-[min(var(--available-height),304px)] overflow-x-hidden overflow-y-auto",
                variant === "outlined" &&
                  (isAdmin
                    ? ["rounded-2xl p-1 shadow-md ring-1 ring-foreground/10"]
                    : // Public site: MUI Select paper — 4px radius, elevation-8 shadow, no inner padding.
                      [
                        "rounded-[4px] py-1",
                        "shadow-[0_5px_5px_-3px_rgba(0,0,0,0.2),_0_8px_10px_1px_rgba(0,0,0,0.14),_0_3px_14px_2px_rgba(0,0,0,0.12)]",
                      ]),
                // MUI Select paper: 4px radius, elevation-8 shadow, no inner padding (list has py-2)
                variant === "standard" && [
                  "rounded-[4px] py-2",
                  "shadow-[0_5px_5px_-3px_rgba(0,0,0,0.2),_0_8px_10px_1px_rgba(0,0,0,0.14),_0_3px_14px_2px_rgba(0,0,0,0.12)]",
                ],
              )}
            >
              <SelectPrimitive.List>
                {items.map((item) => (
                  <SelectPrimitive.Item
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                    data-slot="floating-select-item"
                    className={cn(
                      "relative flex w-full cursor-default items-center outline-hidden select-none",
                      "data-disabled:pointer-events-none data-disabled:opacity-50",
                      variant === "outlined" &&
                        (isAdmin
                          ? [
                              "gap-2 rounded-xl px-3 py-2 pr-8 text-base",
                              "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
                            ]
                          : // Public site: square MUI MenuItem — highlight/selected as translucent
                            // fills, and NO check indicator (old MUI shows selection by fill only).
                            [
                              "gap-2 px-4 py-[6px] text-base",
                              "data-[highlighted]:bg-primary/[.04]",
                              "data-[selected]:bg-primary/[.08]",
                            ]),
                      // MUI MenuItem: 16px h-padding, 6px v-padding, no rounding
                      // selected = primary at 8% opacity; highlighted = black at 4%
                      variant === "standard" && [
                        "cursor-pointer px-4 py-[6px] text-[15px]",
                        "data-[highlighted]:bg-primary/[.04]",
                        "data-[selected]:bg-primary/[.08]",
                      ],
                    )}
                  >
                    <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 truncate whitespace-nowrap">
                      {item.label}
                    </SelectPrimitive.ItemText>
                    {variant === "outlined" && isAdmin && (
                      <SelectPrimitive.ItemIndicator
                        render={
                          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
                        }
                      >
                        <CheckIcon className="pointer-events-none size-4" />
                      </SelectPrimitive.ItemIndicator>
                    )}
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.List>
            </SelectPrimitive.Popup>
          </SelectPrimitive.Positioner>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {error ? (
        <p id={errorId} className="pl-3 text-xs text-destructive">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="pl-3 text-xs text-muted-foreground">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

export { FloatingSelect };
export type { FloatingSelectItem, FloatingSelectProps };
